// src/components/events/TestingEventDialog.jsx
import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  FormControlLabel,
  Switch,
  MenuItem,
  Typography,
  Divider,
  Box,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { Icon } from "@iconify/react";

import ErrorAlert from "../../components/alerts/ErrorAlert";
import Loading from "../../components/spinners/Loading";

import {
  useGetEventsQuery,
  useCreateEventMutation,
  useAddAttendeesMutation,
} from "../../features/events/eventsApiSlice";
import { useDispatch } from "react-redux";
import { setDialog } from "../../features/dialog/dialogSlice";

function splitMemberIds(members = []) {
  return members
    .map((m) => {
      if (!m) return null;
      if (typeof m === "string") return m;
      if (typeof m === "number") return String(m);
      return m?._id || m?.id || m?.memberId || null;
    })
    .filter(Boolean);
}

export default function TestingEventDialog({
  open,
  onClose,
  selectedMembers = [],
  defaultTitle = "Zur Prüfungs-/Testliste hinzufügen",
}) {
  const dispatch = useDispatch();

  // Load all events; we filter TESTING locally
  const { data: events = [], isLoading, isError } = useGetEventsQuery();

  const [createEvent, { isLoading: isCreating, error: createError }] =
    useCreateEventMutation();
  const [addAttendees, { isLoading: isAdding, error: addError }] =
    useAddAttendeesMutation();

  const testingEvents = useMemo(
    () =>
      Array.isArray(events)
        ? events.filter((e) => e?.eventType === "TESTING")
        : [],
    [events]
  );

  const memberIds = useMemo(
    () => splitMemberIds(selectedMembers),
    [selectedMembers]
  );

  // form
  const {
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      modeCreate: testingEvents.length === 0, // default to create if none exist
      existingEventId: "",
      eventName: "",
      eventDateBegin: "",
      eventDateEnd: "",
      locationName: "",
      city: "",
      zip: "",
      street: "",
    },
  });

  const modeCreate = watch("modeCreate");
  const existingEventId = watch("existingEventId");
  const selectedTestingEvent = useMemo(
    () => testingEvents.find((e) => (e._id || e.id) === existingEventId),
    [testingEvents, existingEventId]
  );

  useEffect(() => {
    // when dialog opens or events change, seed defaults
    if (!open) return;
    reset((prev) => ({
      ...prev,
      modeCreate: testingEvents.length === 0,
      existingEventId:
        testingEvents.length > 0
          ? testingEvents[0]._id || testingEvents[0].id
          : "",
    }));
  }, [open, testingEvents, reset]);

  const onSubmit = async (data) => {
    // Basic guards like in SitesForm
    if (!memberIds.length) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Keine Mitglieder ausgewählt",
          data: { errorText: "Bitte wählen Sie mindestens ein Mitglied aus." },
        })
      );
      return;
    }

    try {
      let targetEventId = data.existingEventId;
      let targetEventName = selectedTestingEvent?.eventName || "Prüfung";

      if (data.modeCreate) {
        // Create new TESTING event (mirrors SitesForm style)
        const payload = {
          eventName: data.eventName?.trim(),
          eventType: "TESTING",
          locationName: data.locationName?.trim() || undefined,
          eventDateBegin: data.eventDateBegin || undefined,
          eventDateEnd: data.eventDateEnd || undefined,
          city: data.city?.trim() || undefined,
          zip: data.zip?.trim() || undefined,
          street: data.street?.trim() || undefined,
        };

        if (!payload.eventName) {
          // react-hook-form validation should already catch this, but keep redundancy
          return;
        }

        const res = await createEvent(payload).unwrap();
        targetEventId = res?.event?._id || res?.event?.id;
        targetEventName = res?.event?.eventName || payload.eventName || "Prüfung";
      } else {
        if (!targetEventId) {
          return;
        }
        const existingEvent = testingEvents.find(
          (e) => (e._id || e.id) === targetEventId
        );
        targetEventName = existingEvent?.eventName || targetEventName;
      }

      await addAttendees({ id: targetEventId, memberIds }).unwrap();

      // SUCCESS dialog (SitesForm pattern)
      dispatch(
        setDialog({
          title: "Mitglieder erfolgreich hinzugefügt",
          type: "SUCCESS",
          data: {
            successText: `${memberIds.length} Mitglied(er) wurden zur Prüfung „${targetEventName}“ hinzugefügt.`,
            iconSize: "24vh",
          },
        })
      );
    } catch (e) {
      // A generic guard; detailed errors are displayed inline as well
      dispatch(
        setDialog({
          title: "Fehler",
          type: "ERROR",
          data: {
            errorText:
              e?.data?.message ||
              "Es ist ein Fehler beim Erstellen der Prüfung oder beim Hinzufügen der Teilnehmer aufgetreten.",
          },
        })
      );
    }
  };

  const busy = isLoading || isCreating || isAdding;

  return (
    <Dialog open={open} onClose={() => onClose?.()} maxWidth="sm" fullWidth>
      <DialogTitle>{defaultTitle}</DialogTitle>
      <DialogContent dividers>
        {busy && (
          <Box sx={{ my: 1 }}>
            <Loading />
          </Box>
        )}

        {isError && (
          <Box sx={{ mb: 1 }}>
            <ErrorAlert errorMessage="Fehler beim Laden der Events." />
          </Box>
        )}

        {createError && (
          <Box sx={{ mb: 1 }}>
            <ErrorAlert errorMessage="Fehler beim Erstellen der Prüfung." />
          </Box>
        )}

        {addError && (
          <Box sx={{ mb: 1 }}>
            <ErrorAlert errorMessage="Fehler beim Hinzufügen der Teilnehmer." />
          </Box>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="form">
          <Stack spacing={2}>
            <Controller
              name="modeCreate"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch {...field} checked={field.value} />}
                  label={
                    field.value
                      ? "Neues Prüfungs-Event erstellen"
                      : "Bestehendes Prüfungs-Event wählen"
                  }
                />
              )}
            />

            {!modeCreate && (
              <Controller
                name="existingEventId"
                control={control}
                rules={{ required: "Bitte eine Prüfung auswählen." }}
                render={({ field }) => (
                  <TextField
                    select
                    label="Prüfung auswählen"
                    value={field.value}
                    onChange={field.onChange}
                    error={!!errors.existingEventId}
                    helperText={
                      errors.existingEventId?.message ||
                      `${testingEvents.length} Prüfung(en) gefunden.`
                    }
                  >
                    {testingEvents.map((e) => (
                      <MenuItem key={e._id || e.id} value={e._id || e.id}>
                        {e.eventName}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            )}

            {modeCreate && (
              <>
                <Controller
                  name="eventName"
                  control={control}
                  rules={{
                    required: "Bitte einen Namen für die Prüfung angeben.",
                  }}
                  render={({ field }) => (
                    <TextField
                      label="Name der Prüfung"
                      value={field.value}
                      onChange={field.onChange}
                      error={!!errors.eventName}
                      helperText={errors.eventName?.message}
                      required
                    />
                  )}
                />

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Controller
                    name="eventDateBegin"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="Beginn"
                        type="datetime-local"
                        value={field.value}
                        onChange={field.onChange}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    name="eventDateEnd"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="Ende (optional)"
                        type="datetime-local"
                        value={field.value}
                        onChange={field.onChange}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                    )}
                  />
                </Stack>

                <Divider />

                <Controller
                  name="locationName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Ort / Halle"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Controller
                    name="city"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="Stadt"
                        value={field.value}
                        onChange={field.onChange}
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    name="zip"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="PLZ"
                        value={field.value}
                        onChange={field.onChange}
                        fullWidth
                      />
                    )}
                  />
                </Stack>

                <Controller
                  name="street"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Straße"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </>
            )}

            <Box>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Ausgewählte Mitglieder: <strong>{memberIds.length}</strong>
              </Typography>
            </Box>
          </Stack>

          <DialogActions sx={{ px: 0, mt: 2 }}>
            <Button onClick={() => onClose?.()} color="inherit">
              Abbrechen
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="secondary"
              startIcon={<Icon icon="mdi:playlist-plus" />}
              disabled={
                busy ||
                (!modeCreate && !existingEventId) ||
                (modeCreate && !!errors.eventName)
              }
              fullWidth
              sx={{ fontSize: "110%" }}
            >
              Zur Prüfliste hinzufügen
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
}

TestingEventDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  selectedMembers: PropTypes.array,
  defaultTitle: PropTypes.string,
};
